import { Environment, ENVIRONMENT_DI } from './../environment/environment';
import { LoaderService } from './loader.service';
import { AsyncHelper } from './../../framework/async-helper';
import { Logger, LoggerFactory } from './logger.service';
import { SiteContextService } from './site-context.service';
import { ResourceFillerService } from './identity/resource-filler.service';
import { Inject, Injectable, EventEmitter } from '@angular/core';

export enum ApplicationEvent {
  Initialized,
}
// Initializes necesessary services, triggers application initialized event when everything is ready.
@Injectable()
export class ApplicationStarterService {
  public readonly applicationEvents = new EventEmitter<ApplicationEvent>();
  private isViewInitialized: boolean;
  private readonly logger: Logger;

  constructor(
    loggerFactory: LoggerFactory,
    private readonly resourceFiller: ResourceFillerService,
    private readonly siteContext: SiteContextService,
    private readonly loaderService: LoaderService,
    @Inject(ENVIRONMENT_DI) private readonly environment: Environment,
  ) {
    this.logger = loggerFactory.get('ApplicationStarterService');
  }

  public initializeAsync(): Promise<void> {
    // initialize resource filler
    return this.waitForAppInit({
      waitForSiteContext: true,
      waitForResourceFiller: true,
      waitForViewInitialization: true,
      waitForInitialRequests: false,
    }).then(() => this.triggerApplicationInitialized());
  }

  // Used by AppComponent to notify the view is ready.
  public notifyViewIsInitialized() {
    this.isViewInitialized = true;
  }

  private waitForAppInit(settings: WaitSettings): Promise<void> {
    return (
      new SequentialTaskRunner(this.logger)
        // Initialize site context
        .addTask('Site context', () => this.siteContext.initializeAsync(), settings.waitForSiteContext)
        // Then resource filler to fill missing resources in identity
        .addTask('Resource filler', () => this.resourceFiller.initializeAsync(), settings.waitForResourceFiller)
        // Wait for the view to be initialized
        .addTask(
          'View',
          () => AsyncHelper.waitUntilTrueAsync(() => this.isViewInitialized),
          settings.waitForViewInitialization,
        )
        // wait for all initial requests to be done
        .addTask(
          'Initial requests',
          () => this.loaderService.waitForCurrentBusyStateToEndAsync(),
          settings.waitForInitialRequests,
        )
        .executeAll()
    );
  }

  private triggerApplicationInitialized(): void {
    // Notify angular context
    this.applicationEvents.emit(ApplicationEvent.Initialized);
    // Notify JS context
    ApplicationStarterService.triggerEvent('applicationInitialized');
    this.logger.info('Application is initialized');
  }

  public static triggerEvent<T>(eventName: string, data?: T): void {
    const event = new CustomEvent(eventName, { detail: data });
    document.dispatchEvent(event);
  }
}

interface WaitSettings {
  waitForSiteContext: boolean;
  waitForResourceFiller: boolean;
  waitForViewInitialization: boolean;
  waitForInitialRequests: boolean;
}

class SequentialTaskRunner {
  private readonly tasks = new Array<() => Promise<void>>();
  constructor(private readonly logger: Logger) {}

  public addTask(name: string, task: () => Promise<void>, willInitialize: boolean): SequentialTaskRunner {
    this.tasks.push(() => this.getTaskFor(name, task, willInitialize));
    return this;
  }

  public async executeAll(): Promise<void> {
    // Execute all tasks
    for (let i = 0; i < this.tasks.length; i++) {
      const task = this.tasks[i];
      await task();
    }
  }

  private getTaskFor(name: string, task: () => Promise<void>, willInitialize: boolean): Promise<void> {
    if (willInitialize) {
      this.logger.debug(`Waiting for ${name} to be initalized.`);
      return task()
        .then(() => this.logger.debug(`${name} is initialized.`))
        .catch((reason: any) => this.logger.error(`Initialization failed for ${name} because of ${reason}`));
    }
    return Promise.resolve().then(() =>
      this.logger.debug(`${name} will not be waited for application initialization.`),
    );
  }
}
